import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { getSimplePlaceholder } from '../utils/placeholder';

function CartDrawer({ isOpen, onClose, cart, onUpdateQuantity, onRemoveItem, user, onOpenAuthModal }) {
  const navigate = useNavigate();
  const { showInfo } = useToast();
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Generate unique cart item ID based on product ID, color, and size
  const getCartItemId = (item) => {
    const color = item.selectedColor || '';
    const size = item.selectedSize || '';
    return `${item.id}_${color}_${size}`;
  };

  // Default: select all items whenever drawer is opened
  useEffect(() => {
    if (isOpen) {
      setSelectedItems(new Set(cart.map(item => getCartItemId(item))));
    } else {
      setSelectedItems(new Set());
    }
  }, [isOpen, cart]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Toggle select all
  const handleSelectAll = () => {
    if (selectedItems.size === cart.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cart.map(item => getCartItemId(item))));
    }
  };

  // Toggle individual item selection
  const handleToggleItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Calculate total only for selected items
  const selectedCartItems = cart.filter(item => selectedItems.has(getCartItemId(item)));
  const total = selectedCartItems.reduce((sum, item) => {
    const price = parseFloat(item.price) || 0;
    const quantity = item.quantity || 1;
    return sum + (price * quantity);
  }, 0);

  const totalItems = selectedCartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const allSelected = cart.length > 0 && selectedItems.size === cart.length;
  const someSelected = selectedItems.size > 0 && selectedItems.size < cart.length;

  return (
    <>
      {/* Backdrop with blur effect */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] lg:w-[480px] bg-white shadow-2xl z-[70] transform translate-x-0 transition-transform duration-300 ease-in-out overflow-hidden flex flex-col">
        {/* Elegant Header with Gradient */}
        <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div className="flex items-center gap-2 sm:gap-2.5">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Shopping Cart</h2>
                <p className="text-xs text-white/80 mt-0.5">
                  {cart.length} {cart.length === 1 ? 'item' : 'items'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all backdrop-blur-sm hover:scale-110 shadow-lg"
              aria-label="Close cart"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {cart.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer select-none bg-white/10 hover:bg-white/20 rounded-lg px-2.5 py-1.5 transition-all backdrop-blur-sm">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected;
                }}
                onChange={handleSelectAll}
                className="w-4 h-4 text-primary border-white/30 rounded focus:ring-2 focus:ring-white/50 cursor-pointer bg-white/20"
              />
              <span className="text-xs sm:text-sm font-semibold text-white">
                Select All ({cart.length} {cart.length === 1 ? 'item' : 'items'})
              </span>
            </label>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6 bg-gray-50/50">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h3>
              <p className="text-sm sm:text-base text-gray-500 mb-6">Start adding items to your cart</p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {cart.map((item) => {
                const itemPrice = parseFloat(item.price) || 0;
                const quantity = item.quantity || 1;
                const itemTotal = itemPrice * quantity;
                const cartItemId = getCartItemId(item);
                const isSelected = selectedItems.has(cartItemId);

                return (
                  <div 
                    key={cartItemId} 
                    className={`group relative bg-white rounded-xl p-3 sm:p-4 transition-all duration-300 ${
                      isSelected 
                        ? 'ring-2 ring-primary shadow-lg shadow-primary/20 scale-[1.02]' 
                        : 'shadow-md hover:shadow-lg'
                    }`}
                  >
                    <div className="flex gap-3 sm:gap-4">
                      {/* Checkbox */}
                      <div className="flex items-start pt-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleItem(cartItemId)}
                          className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary/50 cursor-pointer"
                        />
                      </div>

                      {/* Product Image */}
                      <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 ring-2 ring-gray-200 group-hover:ring-primary/30 transition-all">
                        <img 
                          src={item.image || getSimplePlaceholder(200, 200)} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = getSimplePlaceholder(200, 200);
                          }}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate mb-1.5 group-hover:text-primary transition-colors">
                          {item.name}
                        </h3>
                        
                        {/* Color and Size if available */}
                        {(item.selectedColor || item.selectedSize) && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {item.selectedColor && (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-medium">
                                {item.selectedColor}
                              </span>
                            )}
                            {item.selectedSize && (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-medium">
                                Size: {item.selectedSize}
                              </span>
                            )}
                          </div>
                        )}

                        <p className="text-base sm:text-lg font-bold text-primary mb-3">
                          GH¢{itemPrice.toFixed(2)}
                        </p>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onUpdateQuantity(cartItemId, quantity - 1)}
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border-2 border-gray-300 flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white transition-all text-gray-600 group/btn"
                            aria-label="Decrease quantity"
                          >
                            <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="text-sm sm:text-base font-bold text-gray-900 min-w-[2.5rem] text-center bg-gray-50 px-2 py-1 rounded-lg">
                            {quantity}
                          </span>
                          <button
                            onClick={() => onUpdateQuantity(cartItemId, quantity + 1)}
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border-2 border-gray-300 flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white transition-all text-gray-600 group/btn"
                            aria-label="Increase quantity"
                          >
                            <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Remove Button & Total */}
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => onRemoveItem(cartItemId)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-all text-gray-400 hover:text-red-600 group/remove"
                          aria-label="Remove item"
                        >
                          <svg className="w-5 h-5 group-hover/remove:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <div className="mt-auto text-right">
                          <p className="text-xs text-gray-500 mb-0.5">Subtotal</p>
                          <p className="text-base sm:text-lg font-bold text-gray-900">
                            GH¢{itemTotal.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Elegant Footer */}
        {cart.length > 0 && (
          <div className="border-t-2 border-gray-200 bg-white px-4 sm:px-6 py-5 sm:py-6 flex-shrink-0 shadow-2xl">
            <div className="mb-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm sm:text-base font-semibold text-gray-700">
                  {selectedItems.size > 0 ? (
                    <>Selected Items ({totalItems} {totalItems === 1 ? 'item' : 'items'}):</>
                  ) : (
                    <>Total Items ({cart.length} {cart.length === 1 ? 'item' : 'items'}):</>
                  )}
                </span>
                {selectedItems.size > 0 ? (
                  <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
                    GH¢{total.toFixed(2)}
                  </span>
                ) : (
                  <span className="text-lg sm:text-xl font-bold text-gray-400">
                    Select items
                  </span>
                )}
              </div>
              {selectedItems.size > 0 && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-green-600 font-medium">Ready to checkout</span>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => {
                if (selectedItems.size > 0) {
                  const selectedCartItems = cart.filter(item => selectedItems.has(getCartItemId(item)));
                  if (user) {
                    onClose();
                    navigate('/checkout', { state: { selectedItems: selectedCartItems } });
                  } else {
                    // Open auth modal for user to login/register
                    if (onOpenAuthModal) {
                      onOpenAuthModal();
                    } else {
                      // Fallback to toast if onOpenAuthModal is not provided
                      showInfo('Please log in to proceed to checkout');
                    }
                  }
                }
              }}
              className={`w-full py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] ${
                selectedItems.size > 0
                  ? 'bg-gradient-to-r from-primary via-primary-dark to-secondary text-white hover:from-primary-dark hover:to-primary'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              disabled={selectedItems.size === 0}
            >
              {selectedItems.size > 0 ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  Proceed to Checkout
                  <span className="bg-white/20 px-2 py-0.5 rounded-lg text-sm">
                    ({selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'})
                  </span>
                </span>
              ) : (
                'Select items to checkout'
              )}
            </button>
            
            {selectedItems.size > 0 && (
              <button
                onClick={() => {
                  selectedCartItems.forEach(item => onRemoveItem(getCartItemId(item)));
                  setSelectedItems(new Set());
                }}
                className="w-full mt-3 py-2.5 text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all border border-red-200 hover:border-red-300"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove Selected ({selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'})
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default CartDrawer;
